import Draw from 'ol/interaction/Draw'
import Overlay from 'ol/Overlay'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import { getArea, getLength } from 'ol/sphere'
import { unByKey } from 'ol/Observable'
import { Fill, Stroke, Style, Circle as CircleStyle } from 'ol/style'
import type Map from 'ol/Map'
import type { EventsKey } from 'ol/events'
import type { LineString, Polygon } from 'ol/geom'

/**
 * Measure tools (PRD P1.2): geodesic distance and area via ol/sphere on
 * sketches drawn with ol/interaction/Draw. Results stay on the map as static
 * tooltips until cleared. The app shell checks `isActive()` in its map click
 * handler so measuring never fights the GetFeatureInfo popup.
 */
export type MeasureMode = 'distance' | 'area'

export type DistanceUnit = 'km' | 'm'
export type AreaUnit = 'km2' | 'ha' | 'm2'

export interface MeasureTool {
  isActive(): boolean
  mode(): MeasureMode | null
  setMode(mode: MeasureMode | null): void
  setDistanceUnit(unit: DistanceUnit): void
  setAreaUnit(unit: AreaUnit): void
  clear(): void
  hasResults(): boolean
}

export function formatDistance(meters: number, unit: DistanceUnit): string {
  if (unit === 'm') return `${Math.round(meters).toLocaleString()} m`
  return `${(meters / 1000).toFixed(meters < 100_000 ? 2 : 1)} km`
}

export function formatArea(m2: number, unit: AreaUnit): string {
  if (unit === 'm2') return `${Math.round(m2).toLocaleString()} m²`
  if (unit === 'ha') return `${(m2 / 10_000).toFixed(2)} ha`
  return `${(m2 / 1_000_000).toFixed(m2 < 100_000_000 ? 3 : 1)} km²`
}

const SKETCH_STYLE = new Style({
  stroke: new Stroke({ color: '#0b7285', width: 2, lineDash: [6, 6] }),
  fill: new Fill({ color: 'rgba(11, 114, 133, 0.12)' }),
  image: new CircleStyle({ radius: 4, fill: new Fill({ color: '#0b7285' }) }),
})
const RESULT_STYLE = new Style({
  stroke: new Stroke({ color: '#0b7285', width: 2 }),
  fill: new Fill({ color: 'rgba(11, 114, 133, 0.12)' }),
})

export function createMeasure(map: Map, onModeChange: (mode: MeasureMode | null) => void): MeasureTool {
  const source = new VectorSource()
  const layer = new VectorLayer({ source, style: RESULT_STYLE, zIndex: 20 })
  map.addLayer(layer)

  let draw: Draw | null = null
  let currentMode: MeasureMode | null = null
  let distanceUnit: DistanceUnit = 'km'
  let areaUnit: AreaUnit = 'km2'
  // Finished measurements: tooltip overlay + raw geodesic value, kept so a
  // unit change can re-format everything already on the map.
  const results: Array<{ overlay: Overlay; meters: number; mode: MeasureMode }> = []

  function formatFor(mode: MeasureMode, value: number): string {
    return mode === 'distance' ? formatDistance(value, distanceUnit) : formatArea(value, areaUnit)
  }

  function makeTooltip(): { overlay: Overlay; element: HTMLElement } {
    const element = document.createElement('div')
    element.className = 'measure-tooltip'
    const overlay = new Overlay({ element, offset: [0, -12], positioning: 'bottom-center' })
    map.addOverlay(overlay)
    return { overlay, element }
  }

  function refreshResultLabels(): void {
    for (const r of results) {
      const el = r.overlay.getElement()
      if (el) el.textContent = formatFor(r.mode, r.meters)
    }
  }

  function teardownDraw(): void {
    if (draw) {
      map.removeInteraction(draw)
      draw = null
    }
  }

  function setMode(mode: MeasureMode | null): void {
    if (mode === currentMode) mode = null // clicking the active tool toggles it off
    teardownDraw()
    currentMode = mode
    onModeChange(mode)
    if (!mode) return

    draw = new Draw({
      source,
      type: mode === 'distance' ? 'LineString' : 'Polygon',
      style: SKETCH_STYLE,
    })
    map.addInteraction(draw)

    let sketchListener: EventsKey | null = null
    let live: { overlay: Overlay; element: HTMLElement } | null = null

    draw.on('drawstart', (event) => {
      live = makeTooltip()
      const geometry = event.feature.getGeometry() as LineString | Polygon
      sketchListener = geometry.on('change', () => {
        const { meters, at } =
          geometry.getType() === 'LineString'
            ? {
                meters: getLength(geometry),
                at: (geometry as LineString).getLastCoordinate(),
              }
            : {
                meters: getArea(geometry),
                at: (geometry as Polygon).getInteriorPoint().getCoordinates(),
              }
        if (live) {
          live.element.textContent = formatFor(currentMode!, meters)
          live.overlay.setPosition(at)
        }
      })
    })

    draw.on('drawend', (event) => {
      if (sketchListener) unByKey(sketchListener)
      sketchListener = null
      if (!live) return
      const geometry = event.feature.getGeometry() as LineString | Polygon
      const isLine = geometry.getType() === 'LineString'
      const meters = isLine ? getLength(geometry) : getArea(geometry as Polygon)
      live.element.classList.add('measure-tooltip--static')
      live.element.textContent = formatFor(currentMode!, meters)
      results.push({ overlay: live.overlay, meters, mode: currentMode! })
      live = null
    })

    draw.on('drawabort', () => {
      if (sketchListener) unByKey(sketchListener)
      sketchListener = null
      if (live) {
        map.removeOverlay(live.overlay)
        live = null
      }
    })
  }

  // Esc aborts the in-progress sketch without leaving measure mode.
  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && draw) draw.abortDrawing()
  }
  document.addEventListener('keydown', onKeyDown)

  return {
    isActive: () => currentMode !== null,
    mode: () => currentMode,
    setMode,
    setDistanceUnit(unit) {
      distanceUnit = unit
      refreshResultLabels()
    },
    setAreaUnit(unit) {
      areaUnit = unit
      refreshResultLabels()
    },
    clear() {
      source.clear()
      for (const r of results) map.removeOverlay(r.overlay)
      results.length = 0
    },
    hasResults: () => results.length > 0,
  }
}
